import axios from 'axios';
import { extract_error_message, parse_response_data } from '@/utils/api_response_helpers';
import {
  ASSIGN_TEMPLATE_TO_TEMPLATE_FORM,
  GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
  GET_TEMPLATE_FORM_TEMPLATE_ASSIGNMENTS_URL,
  GET_TEMPLATE_FORMS_URL,
  UNASSIGN_TEMPLATE_FROM_TEMPLATE_FORM,
  get_request_config,
} from '@/utils/system_constants/constant';
import { normalize_template_form } from './esigning_setup_helpers';

const esigning_setup_form_methods = {
  assign_form(form_id) {
    this.selected_form_id = form_id;
  },

  is_form_assigned_to_template(form_id) {
    return Boolean(this.template_form_assignments[String(form_id)]?.is_active);
  },

  async fetch_template_form_assignment(template_form) {
    if (!template_form?.id) return template_form;

    try {
      const response = await axios.get(
        GET_ONBOARDING_TEMPLATE_FORM_ASSIGNMENTS_URL,
        get_request_config({
          params: { template_form_id: template_form.id },
        }),
      );

      const parsed = parse_response_data(response);
      const assignment_items = Array.isArray(parsed?.data) ? parsed.data : [];
      const active_assignment = assignment_items.find((item) => item?.is_active !== false) || null;

      return {
        ...template_form,
        onboarding_form:
          String(active_assignment?.onboarding_form_name || 'Not linked').trim() ||
          'Not linked',
        onboarding_form_id: String(active_assignment?.onboarding_form_id || '').trim(),
      };
    } catch {
      return {
        ...template_form,
        onboarding_form: 'Not linked',
        onboarding_form_id: '',
      };
    }
  },

  async load_template_form_template_assignments() {
    const template_document_id = Number(this.template_id || 0) || null;

    if (!template_document_id) {
      this.template_form_assignments = {};
      this.selected_form_id = '';
      return;
    }

    try {
      const response = await axios.get(
        GET_TEMPLATE_FORM_TEMPLATE_ASSIGNMENTS_URL,
        get_request_config({
          params: { template_document_id },
        }),
      );

      const parsed = parse_response_data(response);
      const assignment_items = (Array.isArray(parsed?.data) ? parsed.data : []).filter(
        (item) => Number(item?.template_document_id) === template_document_id,
      );
      const assignment_map = {};

      assignment_items.forEach((item) => {
        const template_form_id = String(item?.template_form_id || '').trim();
        if (template_form_id) assignment_map[template_form_id] = item;
      });

      this.template_form_assignments = assignment_map;

      const first_active_assignment = assignment_items.find(
        (item) => item?.is_active !== false,
      );
      this.selected_form_id = first_active_assignment?.template_form_id
        ? String(first_active_assignment.template_form_id)
        : '';
    } catch (error) {
      this.template_form_assignments = {};
      this.selected_form_id = '';
      this.show_error(
        extract_error_message(error, 'Failed to load template form assignments.'),
      );
    }
  },

  async load_template_forms() {
    this.forms_loading = true;

    try {
      const response = await axios.get(
        GET_TEMPLATE_FORMS_URL,
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      const template_forms = (parsed?.data || [])
        .map(normalize_template_form)
        .filter(Boolean);

      this.template_forms = await Promise.all(
        template_forms.map((form) => this.fetch_template_form_assignment(form)),
      );

      await this.load_template_form_template_assignments();

      const has_selected_form = this.template_forms.some(
        (form) => form.id === this.selected_form_id,
      );

      if (!has_selected_form && !this.assigned_form_id) {
        this.selected_form_id = '';
      }
    } catch (error) {
      this.template_forms = [];
      this.selected_form_id = '';
      this.show_error(
        extract_error_message(error, 'Failed to load template forms.'),
      );
    } finally {
      this.forms_loading = false;
    }
  },

  async assign_template_form_to_template(form_id) {
    const template_document_id = Number(this.template_id || 0) || null;
    const template_form_id = Number(form_id || 0) || null;
    const is_unassigning = this.is_form_assigned_to_template(form_id);

    if (!template_document_id) {
      this.show_warning('Template Form', 'Missing template document context for this setup.');
      return;
    }

    if (!template_form_id) {
      this.show_warning('Template Form', 'Please select a valid template form.');
      return;
    }

    if (this.assigning_form_id) return;

    this.assigning_form_id = String(form_id);
    const loader = await this.show_loader(
      is_unassigning ? 'Unassigning template form...' : 'Assigning template form...',
    );

    try {
      const response = await axios.post(
        is_unassigning
          ? UNASSIGN_TEMPLATE_FROM_TEMPLATE_FORM
          : ASSIGN_TEMPLATE_TO_TEMPLATE_FORM,
        {
          template_document_id,
          template_form_id,
          is_active: true,
        },
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      if (String(parsed?.status || '').toLowerCase() !== 'success') {
        this.show_error(
          parsed?.message ||
          (is_unassigning
            ? 'Failed to unassign template form from template.'
            : 'Failed to assign template form to template.'),
        );
        return;
      }

      if (!is_unassigning) {
        this.selected_form_id = String(form_id);
      }

      await this.load_template_form_template_assignments();
      this.set_active_tab('forms');
      this.show_success(
        parsed?.message ||
        (is_unassigning
          ? 'Template unassigned from template form successfully.'
          : 'Template assignment updated successfully.'),
      );
    } catch (error) {
      this.show_error(
        extract_error_message(
          error,
          is_unassigning
            ? 'Failed to unassign template form from template.'
            : 'Failed to assign template form to template.',
        ),
      );
    } finally {
      await this.close_loader(loader);
      this.assigning_form_id = '';
    }
  },
};

export default esigning_setup_form_methods;
